import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import AdminLayout from '../../components/admin/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Settings,
  Save,
  Globe,
  Phone,
  Mail,
  MapPin,
  Facebook,
  Twitter,
  Instagram,
  Linkedin,
  Youtube,
  MessageCircle
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const AdminSettings = () => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    site_name: '',
    site_email: '',
    site_phone: '',
    whatsapp_number: '',
    address: '',
    logo: '',
    favicon: '',
    footer_text: '',
    facebook_url: '',
    twitter_url: '',
    instagram_url: '',
    linkedin_url: '',
    youtube_url: ''
  });

  const fetchSettings = async () => {
    try {
      const response = await fetch(`${API}/api/admin/settings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSettings({
          site_name: data.site_name || '',
          site_email: data.site_email || '',
          site_phone: data.site_phone || '',
          whatsapp_number: data.whatsapp_number || '',
          address: data.address || '',
          logo: data.logo || '',
          favicon: data.favicon || '',
          footer_text: data.footer_text || '',
          facebook_url: data.facebook_url || '',
          twitter_url: data.twitter_url || '',
          instagram_url: data.instagram_url || '',
          linkedin_url: data.linkedin_url || '',
          youtube_url: data.youtube_url || ''
        });
      }
    } catch (error) {
      console.error('Failed to fetch settings:', error);
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const response = await fetch(`${API}/api/admin/settings`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(settings)
      });

      if (response.ok) {
        toast.success('Settings saved successfully');
      } else {
        throw new Error('Failed to save');
      }
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="p-8 text-center text-slate-500">Loading settings...</div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-heading font-bold text-slate-800 flex items-center gap-2">
            <Settings className="w-7 h-7 text-primary" />
            Site Settings
          </h1>
          <p className="text-slate-600">Configure website settings and information</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* General Settings */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Globe className="w-5 h-5 text-primary" />
                General Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="site_name">Site Name</Label>
                  <Input
                    id="site_name"
                    value={settings.site_name}
                    onChange={(e) => setSettings({...settings, site_name: e.target.value})}
                    placeholder="Baobab Hospital"
                    data-testid="settings-site-name"
                  />
                </div>
                <div>
                  <Label htmlFor="site_email">Contact Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="site_email"
                      type="email"
                      value={settings.site_email}
                      onChange={(e) => setSettings({...settings, site_email: e.target.value})}
                      className="pl-10"
                      data-testid="settings-site-email"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="site_phone">Phone Number</Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="site_phone"
                      value={settings.site_phone}
                      onChange={(e) => setSettings({...settings, site_phone: e.target.value})}
                      className="pl-10"
                      data-testid="settings-site-phone"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="whatsapp_number">WhatsApp Number</Label>
                  <div className="relative">
                    <MessageCircle className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="whatsapp_number"
                      value={settings.whatsapp_number}
                      onChange={(e) => setSettings({...settings, whatsapp_number: e.target.value})}
                      className="pl-10"
                      placeholder="+255..."
                      data-testid="settings-whatsapp"
                    />
                  </div>
                </div>
                <div className="md:col-span-2">
                  <Label htmlFor="address">Address</Label>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-3 w-4 h-4 text-slate-400" />
                    <textarea
                      id="address"
                      value={settings.address}
                      onChange={(e) => setSettings({...settings, address: e.target.value})}
                      className="w-full p-2 pl-10 border rounded-lg"
                      rows={2}
                      data-testid="settings-address"
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Branding */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Branding</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="logo">Logo URL</Label>
                  <Input
                    id="logo"
                    value={settings.logo}
                    onChange={(e) => setSettings({...settings, logo: e.target.value})}
                    placeholder="https://..."
                    data-testid="settings-logo"
                  />
                  {settings.logo && (
                    <img src={settings.logo} alt="Logo preview" className="mt-2 h-16 object-contain" />
                  )}
                </div>
                <div>
                  <Label htmlFor="favicon">Favicon URL</Label>
                  <Input
                    id="favicon"
                    value={settings.favicon}
                    onChange={(e) => setSettings({...settings, favicon: e.target.value})}
                    placeholder="https://..."
                    data-testid="settings-favicon"
                  />
                </div>
                <div className="md:col-span-2">
                  <Label htmlFor="footer_text">Footer Text</Label>
                  <textarea
                    id="footer_text"
                    value={settings.footer_text}
                    onChange={(e) => setSettings({...settings, footer_text: e.target.value})}
                    className="w-full p-2 border rounded-lg"
                    rows={2}
                    data-testid="settings-footer-text"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Social Media */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Social Media Links</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="facebook_url">Facebook</Label>
                  <div className="relative">
                    <Facebook className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="facebook_url"
                      value={settings.facebook_url}
                      onChange={(e) => setSettings({...settings, facebook_url: e.target.value})}
                      className="pl-10"
                      placeholder="https://facebook.com/..."
                      data-testid="settings-facebook"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="twitter_url">Twitter</Label>
                  <div className="relative">
                    <Twitter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="twitter_url"
                      value={settings.twitter_url}
                      onChange={(e) => setSettings({...settings, twitter_url: e.target.value})}
                      className="pl-10"
                      placeholder="https://twitter.com/..."
                      data-testid="settings-twitter"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="instagram_url">Instagram</Label>
                  <div className="relative">
                    <Instagram className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="instagram_url"
                      value={settings.instagram_url}
                      onChange={(e) => setSettings({...settings, instagram_url: e.target.value})}
                      className="pl-10"
                      placeholder="https://instagram.com/..."
                      data-testid="settings-instagram"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="linkedin_url">LinkedIn</Label>
                  <div className="relative">
                    <Linkedin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="linkedin_url"
                      value={settings.linkedin_url}
                      onChange={(e) => setSettings({...settings, linkedin_url: e.target.value})}
                      className="pl-10"
                      placeholder="https://linkedin.com/..."
                      data-testid="settings-linkedin"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="youtube_url">YouTube</Label>
                  <div className="relative">
                    <Youtube className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="youtube_url"
                      value={settings.youtube_url}
                      onChange={(e) => setSettings({...settings, youtube_url: e.target.value})}
                      className="pl-10"
                      placeholder="https://youtube.com/..."
                      data-testid="settings-youtube"
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Save Button */}
          <div className="flex justify-end">
            <Button type="submit" className="bg-primary hover:bg-primary-700" disabled={saving} data-testid="settings-save-btn">
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Saving...' : 'Save Settings'}
            </Button>
          </div>
        </form>
      </div>
    </AdminLayout>
  );
};

export default AdminSettings;
